import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, ArrowRight, RefreshCw, Filter,
  Phone, Mail, Video, Users, MessageSquare, FileText, StickyNote } from 'lucide-react';
import { api } from '../services/api';

const ACTION_CFG = {
  create:       { label: 'Criação',      color: 'bg-green-500',  textColor: 'text-green-400',  Icon: Plus },
  update:       { label: 'Edição',       color: 'bg-blue-500',   textColor: 'text-blue-400',   Icon: Pencil },
  delete:       { label: 'Exclusão',     color: 'bg-red-500',    textColor: 'text-red-400',    Icon: Trash2 },
  stage_change: { label: 'Mudança Est.', color: 'bg-amber-500',  textColor: 'text-amber-400',  Icon: ArrowRight },
};

const CANAL_CFG = {
  'Telefone':            { label: 'Telefone',           color: 'bg-sky-500',      textColor: 'text-sky-500',    Icon: Phone },
  'WhatsApp':            { label: 'WhatsApp',            color: 'bg-green-500',    textColor: 'text-green-500',  Icon: MessageSquare },
  'E-mail':              { label: 'E-mail',              color: 'bg-violet-500',   textColor: 'text-violet-500', Icon: Mail },
  'Reunião Presencial':  { label: 'Reunião Presencial',  color: 'bg-orange-500',   textColor: 'text-orange-500', Icon: Users },
  'Videochamada':        { label: 'Videochamada',        color: 'bg-teal-500',     textColor: 'text-teal-500',   Icon: Video },
  'Visita Técnica':      { label: 'Visita Técnica',      color: 'bg-pink-500',     textColor: 'text-pink-500',   Icon: Users },
  'Nota Interna':        { label: 'Nota Interna',        color: 'bg-gray-400',     textColor: 'text-gray-500',   Icon: StickyNote },
};

const FIELD_LABELS = {
  stage: 'Estágio', esn_id: 'ESN', produto_id: 'Produto',
  valor_saas: 'SaaS (R$)', valor_adesao: 'Adesão (R$)', valor_servico: 'Serviço (R$)',
  previsao_fechamento: 'Previsão', score_bant: 'Score BANT',
  contato_nome: 'Contato', titulo: 'Título', razao_social: 'Razão Social',
  cnpj: 'CNPJ', temp: 'Temperatura', motivo_perda: 'Motivo Perda',
  data_fechamento: 'Data Fechamento',
};

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit' });
}

function formatVal(v) {
  if (v === null || v === undefined) return <em className="opacity-40">vazio</em>;
  return <span>{String(v)}</span>;
}

export default function AuditTab({ lead }) {
  const [auditEntries, setAuditEntries] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | stage | interaction

  useEffect(() => {
    if (!lead?.id) return;
    setLoading(true);
    Promise.all([
      api.getLeadAudit(lead.id),
      api.getInteractions(lead.id),
    ]).then(([audit, ints]) => {
      setAuditEntries(audit || []);
      setInteractions(ints || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [lead?.id]);

  // Normalise interactions into timeline events
  const interactionEvents = interactions.map(i => ({
    _source: 'interaction',
    id: `i-${i.id}`,
    action_type: i.canal,
    created_at: i.data_interacao,
    user_name: i.esn_nome || 'ESN',
    descricao: i.descricao,
    proxima_acao_data: i.proxima_acao_data,
    proxima_acao_descricao: i.proxima_acao_descricao,
  }));

  const auditEvents = auditEntries.map(e => ({ _source: 'audit', ...e }));

  // Merge and sort descending
  const allEvents = [...auditEvents, ...interactionEvents].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  const filtered = allEvents.filter(e => {
    if (filter === 'stage')       return e.action_type === 'stage_change';
    if (filter === 'interaction') return e._source === 'interaction';
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-gray-400 text-sm">Carregando histórico...</div>;
  }

  if (filtered.length === 0) {
    return (
      <div className="space-y-3">
        <FilterBar filter={filter} setFilter={setFilter} />
        <div className="flex flex-col items-center justify-center py-10 text-gray-400 text-sm gap-2">
          <RefreshCw size={22} className="opacity-30" />
          Nenhum evento registrado ainda.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <FilterBar filter={filter} setFilter={setFilter} />

      {/* Timeline */}
      <div className="relative ml-4">
        <div className="absolute left-2.5 top-0 bottom-0 w-px bg-gray-200" />
        <div className="space-y-0">
          {filtered.map(entry => {
            const isInteraction = entry._source === 'interaction';
            const isStageChange = entry.action_type === 'stage_change';

            const cfg = isInteraction
              ? (CANAL_CFG[entry.action_type] || { label: entry.action_type, color: 'bg-gray-400', textColor: 'text-gray-500', Icon: FileText })
              : (ACTION_CFG[entry.action_type] || { label: entry.action_type, color: 'bg-gray-400', textColor: 'text-gray-500', Icon: RefreshCw });

            const { Icon } = cfg;

            return (
              <div key={entry.id} className="relative pl-8 pb-4">
                <div className={`absolute left-0 top-1 w-5 h-5 rounded-full ${cfg.color} flex items-center justify-center shadow-sm`}>
                  <Icon size={10} className="text-white" />
                </div>

                <div className={`rounded-lg border p-3 ${
                  isStageChange ? 'bg-amber-50 border-amber-200' :
                  isInteraction  ? 'bg-sky-50 border-sky-100' :
                  'bg-white border-gray-200'
                }`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className={`text-xs font-semibold ${cfg.textColor}`}>{cfg.label}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(entry.created_at)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1.5">
                    por <span className="font-medium text-gray-700">{entry.user_name || 'Sistema'}</span>
                  </p>

                  {/* Interaction body */}
                  {isInteraction && entry.descricao && (
                    <p className="text-xs text-gray-600 mb-1">{entry.descricao}</p>
                  )}
                  {isInteraction && entry.proxima_acao_data && (
                    <p className="text-[10px] text-gray-400">
                      Próxima ação: <span className="font-medium text-gray-600">{entry.proxima_acao_data?.slice(0, 10)}</span>
                      {entry.proxima_acao_descricao && ` — ${entry.proxima_acao_descricao}`}
                    </p>
                  )}

                  {/* Audit body */}
                  {!isInteraction && entry.field_name && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500 font-medium">
                        {FIELD_LABELS[entry.field_name] || entry.field_name}
                      </span>
                      {entry.old_value !== null && (
                        <>
                          <span className="line-through text-red-400">{formatVal(entry.old_value)}</span>
                          <ArrowRight size={10} className="text-gray-400 flex-shrink-0" />
                        </>
                      )}
                      <span className={`font-medium ${isStageChange ? 'text-amber-700' : 'text-gray-700'}`}>
                        {formatVal(entry.new_value)}
                      </span>
                    </div>
                  )}
                  {!isInteraction && !entry.field_name && entry.action_type === 'create' && (
                    <p className="text-xs text-gray-500">Lead criado no funil.</p>
                  )}
                  {!isInteraction && !entry.field_name && entry.action_type === 'delete' && (
                    <p className="text-xs text-red-500">Lead excluído.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FilterBar({ filter, setFilter }) {
  const opts = [
    { value: 'all',         label: 'Todos' },
    { value: 'stage',       label: 'Mudanças de Estágio' },
    { value: 'interaction', label: 'Interações' },
  ];
  return (
    <div className="flex items-center gap-1">
      <Filter size={12} className="text-gray-400" />
      {opts.map(o => (
        <button key={o.value} type="button"
          onClick={() => setFilter(o.value)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
            filter === o.value
              ? 'bg-[#00DBFF]/10 text-[#00DBFF] border border-[#00DBFF]/30'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
